<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  channels: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  selectedChannelId: { type: Number, default: null },
  isLoading: { type: Boolean, default: false },
});

defineEmits(['select']);

const { t } = useI18n();

const channelLabel = channel => {
  if (channel.name) return channel.name;
  if (channel.channel_type === 'dm' && channel.members?.length) {
    return channel.members
      .map(member => member.display_name || member.name)
      .join(', ');
  }
  return t(`INTERNAL_CHAT.CHANNEL_TYPES.${channel.channel_type}`);
};

const categoryName = (channel, categories) =>
  categories.find(category => category.id === channel.category_id)?.name;
</script>

<template>
  <section
    class="flex h-full w-72 shrink-0 flex-col border-e border-n-weak bg-n-background"
  >
    <div
      class="flex items-center justify-between border-b border-n-weak px-4 py-4"
    >
      <h2 class="m-0 text-base font-semibold text-n-slate-12">
        {{ t('INTERNAL_CHAT.CHANNELS') }}
      </h2>
      <span class="text-xs text-n-slate-10">{{ channels.length }}</span>
    </div>
    <div
      v-if="isLoading"
      class="px-4 py-6 text-sm text-n-slate-11"
      role="status"
    >
      {{ t('INTERNAL_CHAT.LOADING') }}
    </div>
    <div v-else-if="!channels.length" class="px-4 py-6 text-sm text-n-slate-11">
      {{ t('INTERNAL_CHAT.EMPTY_CHANNELS') }}
    </div>
    <ul
      v-else
      class="m-0 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2"
      :aria-label="t('INTERNAL_CHAT.CHANNELS')"
    >
      <li v-for="channel in channels" :key="channel.id">
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start transition-colors hover:bg-n-alpha-2"
          :class="
            channel.id === selectedChannelId
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11'
          "
          :aria-current="channel.id === selectedChannelId ? 'page' : undefined"
          :data-testid="`internal-chat-channel-${channel.id}`"
          @click="$emit('select', channel.id)"
        >
          <span
            class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-blue-3 text-n-blue-11"
            aria-hidden="true"
          >
            <span
              :class="
                channel.channel_type === 'dm'
                  ? 'i-lucide-user-round'
                  : channel.channel_type === 'private'
                    ? 'i-lucide-lock-keyhole'
                    : 'i-lucide-hash'
              "
              class="size-4"
            />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium">{{
              channelLabel(channel)
            }}</span>
            <span class="block text-xs text-n-slate-10">
              {{
                categoryName(channel, categories) ||
                t(`INTERNAL_CHAT.CHANNEL_TYPES.${channel.channel_type}`)
              }}
            </span>
          </span>
          <span
            v-if="channel.unread_count > 0"
            class="grid min-w-5 place-items-center rounded-full bg-n-brand px-1 text-xxs font-medium text-white"
          >
            {{
              channel.unread_count > 99
                ? t('INTERNAL_CHAT.UNREAD_OVERFLOW')
                : channel.unread_count
            }}
          </span>
        </button>
      </li>
    </ul>
  </section>
</template>
